import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, first, mergeMap } from 'rxjs/operators';
import { LocalStorageService } from '@us/web/shared/data-access';
import { selectToken } from '../+state/auth.reducer';
import { AccessToken } from '../models';
import { ActivatedRoute, Router } from '@angular/router';
import { isFnBApp, RouterUtil, RouteUtil } from '@us/web/shared/utils';
import { captureException, captureMessage } from '@sentry/angular-ivy';
import { ENVIRONMENT } from '@us/app-config';
import { AuthActions } from '../+state/auth.actions';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
    private readonly AUTH_HEADER = 'X-Authorization';

    private readonly store = inject(Store);
    private readonly localStorage = inject(LocalStorageService);
    private readonly router = inject(Router);
    private readonly env = inject(ENVIRONMENT);
    private readonly route = inject(ActivatedRoute);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let req = request;
        if (!req.headers.has('Content-Type')) {
            req = req.clone({
                headers: req.headers.set('Content-Type', 'application/json')
            });
        }

        if (req.url.includes('/partner/oauth2/token') || req.url.includes('/partner/oauth2/refresh')) {
            return next.handle(req);
        }

        if (!req.headers?.has('shop_id') && (!req.body || !('shop_id' in req.body)) && !req.params?.has('shop_id')) {
            let shopId = this.localStorage.getItem('shop_id');
            if (!shopId && isFnBApp(this.env)) {
                shopId = this.route.snapshot.queryParams['id'];
                this.localStorage.setItem('shop_id', shopId);
                captureMessage('Shop id not found!. Get value from queryParams: ' + shopId, 'debug');
            }

            if (shopId) {
                req = req.clone({
                    headers: req.headers.set('shop_id', shopId),
                    ...(req.method === 'GET'
                        ? {
                              setParams: {
                                  shop_id: shopId
                              }
                          }
                        : {
                              body: {
                                  ...req.body,
                                  shop_id: shopId
                              }
                          })
                });
            } else if (isFnBApp(this.env)) {
                captureMessage('Shop id not found!', 'warning');
            }
        }

        return this.addAuthenticationToken(req).pipe(
            first(),
            mergeMap((requestWithToken: HttpRequest<any>) =>
                next.handle(requestWithToken).pipe(
                    catchError((error: HttpErrorResponse) => {
                        if (error.status === 401) {
                            if (this.router.url === '/account/treatment-card') {
                                this.router.navigateByUrl('/login');
                                return EMPTY;
                            }
                            this.store.dispatch(AuthActions.logoutConfirmed());
                            setTimeout(() => {
                                window.location.reload();
                            }, 100);
                            return EMPTY;
                        }
                        if (error.status === 403) {
                            this.router.navigate([RouteUtil.getLoginRouteUrl()]).then();
                            return EMPTY;
                        }
                        captureException(error);
                        return throwError(() => error);
                    })
                )
            )
        );
    }

    private addAuthenticationToken(request: HttpRequest<any>): Observable<HttpRequest<any>> {
        return this.store.select(selectToken).pipe(
            first(),
            mergeMap((token: AccessToken | null) => {
                let req = request;
                if (token) {
                    req = req.clone({
                        headers: req.headers.set(this.AUTH_HEADER, `Bearer ${token.accessToken}`)
                    });
                } else {
                    console.warn('Token not found!');
                }
                return of(req);
            })
        );
    }
}
